import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { accessDestination } from '../services/accessDestination';
import { Loader2 } from 'lucide-react';
import TotpLoginForm from './TotpLoginForm';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const mounted = useRef(false);
  const completed = useRef(false);
  const attempt = useRef({ cancelled: false, generation: null });
  const { loading, exchangeGoogleSession, verifyTotpLogin, cancelPendingAuthentication,
    isAuthenticationCurrent } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    mounted.current = true;
    // StrictMode repeats setup/cleanup without a real departure. Every setup
    // still needs its own cleanup so the eventual departure cancels the attempt.
    const cleanup = () => {
      mounted.current = false;
      queueMicrotask(() => {
        if (!mounted.current && !completed.current) {
          cancelPendingAuthentication(attempt.current);
        }
      });
    };
    // Bootstrap owns the initial generation; wait before starting an exchange.
    if (loading || hasProcessed.current) return cleanup;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const sessionId = new URLSearchParams(location.hash.substring(1)).get('session_id');
        window.history.replaceState(null, '', window.location.pathname);
        if (!sessionId) {
          completed.current = true;
          navigate('/login');
          return;
        }
        const result = await exchangeGoogleSession(sessionId, attempt.current);
        if (!mounted.current || !isAuthenticationCurrent(attempt.current)) return;
        if (result.totpRequired) {
          setChallenge({ pendingToken: result.pendingToken, message: result.message });
          return;
        }
        const destination = await accessDestination(result.user);
        if (!mounted.current || !isAuthenticationCurrent(attempt.current)) return;
        completed.current = true;
        navigate(destination, { replace: true });
      } catch {
        if (mounted.current && isAuthenticationCurrent(attempt.current)) {
          completed.current = true;
          navigate('/login');
        }
      }
    };
    processSession();
    return cleanup;
  }, [loading, navigate, location, exchangeGoogleSession, cancelPendingAuthentication, isAuthenticationCurrent]);

  const verifyChallenge = async (pending, code, backup) => {
    setIsVerifying(true);
    setError('');
    try {
      const result = await verifyTotpLogin(pending, code, backup, attempt.current);
      if (!mounted.current || !isAuthenticationCurrent(attempt.current)) return;
      const destination = await accessDestination(result.user);
      if (!mounted.current || !isAuthenticationCurrent(attempt.current)) return;
      completed.current = true;
      setChallenge(null);
      navigate(destination, { replace: true });
    } catch {
      if (mounted.current && isAuthenticationCurrent(attempt.current)) {
        setError('No se pudo verificar el código. Intenta de nuevo.');
      }
    } finally {
      if (mounted.current) setIsVerifying(false);
    }
  };

  const cancelChallenge = () => {
    completed.current = true;
    cancelPendingAuthentication(attempt.current);
    setChallenge(null);
    navigate('/login', { replace: true });
  };

  if (challenge) {
    return (
      <div className="upim-halo relative flex min-h-screen items-center justify-center bg-black p-4">
        <div className="w-full max-w-md rounded-lg border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] p-6">
          {error && <p role="alert" className="mb-4 text-sm text-red-300">{error}</p>}
          <TotpLoginForm pendingToken={challenge.pendingToken} message={challenge.message}
            isLoading={isVerifying} onVerify={verifyChallenge} onCancel={cancelChallenge} />
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-black" role="status">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--upim-acento)] mx-auto mb-4" />
        <p className="text-[var(--upim-texto-2)]">Procesando autenticación...</p>
      </div>
    </div>
  );
};
export default AuthCallback;
